import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { HR } from 'foundations-components/transitional/components/HR';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { MARS_LAYER } from 'foundations-components/transitional/constants';
import { LinkIcon } from 'foundations-assets/icons/components/LinkIcon';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { usePortalNavigation } from '../hooks/usePortalNavigation';
import { getNavLabel } from '../constants/navLabels';
import { getIsThemePreview } from '../utils/metadata';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { PortalsSideNavIcon } from './PortalsNav/PortalsSideNavIcon';
import { NavChevron } from './NavChevron';
import { PortalLogo } from './PortalLogo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DRAWER_WIDTH = 320;
function NavDrawerLinkRow({
  item,
  history,
  onNavigate,
  variant,
  sectionActive = false
}) {
  var _item$href, _item$label;
  const isPill = useIsPillBorderRadius();
  const isEmphasized = variant === 'top-level' ? !!item.isActive : sectionActive;
  const handleClick = e => {
    if (history && item.isCurrent && item.path) {
      e.preventDefault();
      history.push(item.path);
      onNavigate();
    } else if (getIsThemePreview()) {
      e.preventDefault();
    }
  };
  return /*#__PURE__*/_jsx(Flex, {
    as: "li",
    backgroundColor: item.isActive ? 'fill-accent-neutral-subtle-alt' : undefined,
    borderRadius: isPill ? '0' : '100',
    marginInlineStart: variant === 'child' ? '1000' : undefined,
    "data-test-id": `portals-nav-drawer-item-${item.id}`,
    children: /*#__PURE__*/_jsxs(Link, {
      href: (_item$href = item.href) !== null && _item$href !== void 0 && _item$href.startsWith('/') ? item.href : `//${item.href}`,
      use: "unstyled",
      display: "flex",
      alignItems: "center",
      gap: "300",
      inlineSize: "100%",
      paddingBlock: "200",
      paddingInlineStart: "300",
      paddingInlineEnd: "300",
      onClick: handleClick,
      "aria-current": item.isActive ? 'page' : undefined,
      children: [variant === 'top-level' ? /*#__PURE__*/_jsx(PortalsSideNavIcon, {
        name: item.id
      }) : null, /*#__PURE__*/_jsx(View, {
        as: "span",
        typography: "body-100",
        color: isEmphasized ? 'text-core-default' : 'text-core-subtle',
        children: /*#__PURE__*/_jsx("span", {
          style: {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          children: (_item$label = item.label) !== null && _item$label !== void 0 ? _item$label : getNavLabel(item.id)
        })
      })]
    })
  });
}
function NavDrawerSection({
  item,
  history,
  onNavigate
}) {
  var _item$label2;
  const isPill = useIsPillBorderRadius();
  const [isOpen, setIsOpen] = useState(!!item.isActive);
  useEffect(() => {
    if (item.isActive) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(true);
    }
  }, [item.isActive]);
  return /*#__PURE__*/_jsxs(View, {
    as: "li",
    "data-test-id": `portals-nav-drawer-section-${item.id}`,
    children: [/*#__PURE__*/_jsx(Clickable, {
      onClick: () => setIsOpen(prev => !prev),
      "aria-expanded": isOpen,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "300",
        paddingBlock: "200",
        paddingInline: "300",
        backgroundColor: item.isActive ? 'fill-accent-neutral-subtle-alt' : undefined,
        borderRadius: isPill ? '0' : '100',
        children: [/*#__PURE__*/_jsx(PortalsSideNavIcon, {
          name: item.id
        }), /*#__PURE__*/_jsx(View, {
          as: "span",
          typography: "body-100",
          color: item.isActive ? 'text-core-default' : 'text-core-subtle',
          children: (_item$label2 = item.label) !== null && _item$label2 !== void 0 ? _item$label2 : getNavLabel(item.id)
        }), /*#__PURE__*/_jsx(Flex, {
          flexGrow: "1"
        }), /*#__PURE__*/_jsx(NavChevron, {
          open: isOpen
        })]
      })
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: isOpen,
      children: /*#__PURE__*/_jsx(Flex, {
        as: "ul",
        flexDirection: "column",
        gap: "100",
        paddingBlockStart: "100",
        children: item.children.map(child => /*#__PURE__*/_jsx(NavDrawerLinkRow, {
          item: child,
          history: history,
          onNavigate: onNavigate,
          variant: "child",
          sectionActive: !!item.isActive
        }, child.id))
      })
    })]
  });
}
function NavDrawerSiteMenuItem({
  item,
  level,
  onNavigate
}) {
  var _item$linkTarget;
  const isPill = useIsPillBorderRadius();
  const hasChildren = item.children && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const inlineStart = level > 0 ? '300' : '1000';
  const inlineMargin = level > 0 ? '1000' : undefined;
  if (hasChildren) {
    return /*#__PURE__*/_jsxs(View, {
      as: "li",
      marginInlineStart: inlineMargin,
      "data-test-id": `portals-nav-drawer-sitemenu-label-${item.label}`,
      children: [/*#__PURE__*/_jsx(Clickable, {
        onClick: () => setIsOpen(prev => !prev),
        "aria-expanded": isOpen,
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "300",
          paddingBlock: "200",
          paddingInlineStart: inlineStart,
          paddingInlineEnd: "300",
          children: [/*#__PURE__*/_jsx(View, {
            as: "span",
            typography: "body-100",
            color: "text-core-subtle",
            children: item.label
          }), /*#__PURE__*/_jsx(Flex, {
            flexGrow: "1"
          }), /*#__PURE__*/_jsx(NavChevron, {
            open: isOpen
          })]
        })
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: isOpen,
        children: /*#__PURE__*/_jsx(Flex, {
          as: "ul",
          flexDirection: "column",
          gap: "100",
          paddingBlockStart: "100",
          listStyleType: "none",
          children: item.children.map((child, index) => /*#__PURE__*/_jsx(NavDrawerSiteMenuItem, {
            item: child,
            level: level + 1,
            onNavigate: onNavigate
          }, `${child.label}-${index}`))
        })
      })]
    });
  }
  if (item.isNonLinkingNode || !item.url) {
    return /*#__PURE__*/_jsx(View, {
      as: "li",
      marginInlineStart: inlineMargin,
      "data-test-id": `portals-nav-drawer-sitemenu-label-${item.label}`,
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        gap: "300",
        paddingBlock: "200",
        paddingInlineStart: inlineStart,
        paddingInlineEnd: "300",
        children: /*#__PURE__*/_jsx(View, {
          as: "span",
          typography: "detail-100",
          color: "text-core-subtle",
          children: item.label
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    as: "li",
    borderRadius: isPill ? '0' : '100',
    marginInlineStart: inlineMargin,
    "data-test-id": `portals-nav-drawer-sitemenu-item-${item.label}`,
    children: /*#__PURE__*/_jsx(Link, {
      href: item.url,
      external: item.linkTarget === '_blank',
      target: (_item$linkTarget = item.linkTarget) !== null && _item$linkTarget !== void 0 ? _item$linkTarget : undefined,
      use: "unstyled",
      display: "flex",
      alignItems: "center",
      gap: "300",
      inlineSize: "100%",
      paddingBlock: "200",
      paddingInlineStart: inlineStart,
      paddingInlineEnd: "300",
      onClick: () => onNavigate(),
      children: /*#__PURE__*/_jsx(View, {
        as: "span",
        typography: "body-100",
        color: "text-core-subtle",
        children: item.label
      })
    })
  });
}
export function PortalsNavDrawer({
  open,
  onClose,
  history,
  currentPathname
}) {
  const {
    navItems,
    siteMenuData
  } = usePortalNavigation(currentPathname);
  const previouslyFocusedRef = useRef(null);

  // Capture the focused element at render time, before child Panel/Portal
  // mounts steal focus on their own commit. This must run during render so
  // that the captured element is the trigger that opened the drawer rather
  // than the panel surface.
  // eslint-disable-next-line react-hooks/refs
  if (open && previouslyFocusedRef.current == null) {
    previouslyFocusedRef.current = document.activeElement;
  }
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const toFocus = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (toFocus && typeof toFocus.focus === 'function') {
        toFocus.focus();
      }
    };
  }, [open, onClose]);
  if (!open) return null;
  const backdrop = /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(View, {
    onClick: onClose,
    "aria-hidden": "true",
    "data-test-id": "portals-nav-drawer-backdrop",
    position: "fixed",
    inset: "0",
    zIndex: MARS_LAYER,
    backgroundColor: "specialty-scrim"
  }), document.body);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [backdrop, /*#__PURE__*/_jsx(Panel, {
      align: "left",
      width: DRAWER_WIDTH,
      "aria-label": unescapedText('app.navDrawer.label'),
      "data-test-id": "portals-nav-drawer",
      children: /*#__PURE__*/_jsxs(View, {
        inlineSize: "100%",
        id: "portals-nav-drawer",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          blockSize: "56px",
          paddingInline: "200",
          gap: "200",
          "data-test-id": "portals-nav-drawer-header",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexShrink: "0",
            alignItems: "center",
            minInlineSize: "0",
            children: /*#__PURE__*/_jsx(PortalLogo, {
              height: "32px",
              maxWidth: "120px",
              fallbackMaxInlineSize: "250px"
            })
          }), /*#__PURE__*/_jsx(Flex, {
            flexGrow: "1"
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            "data-action": "close",
            onClick: onClose,
            color: "text-core-default",
            "aria-label": unescapedText('app.navDrawer.close'),
            "data-test-id": "portals-nav-drawer-close",
            marginInlineEnd: "200"
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(PanelBody, {
          children: [/*#__PURE__*/_jsx(Flex, {
            as: "ul",
            flexDirection: "column",
            gap: "100",
            paddingInline: "200",
            children: navItems.map(item => item.children.length > 0 ? /*#__PURE__*/_jsx(NavDrawerSection, {
              item: item,
              history: history,
              onNavigate: onClose
            }, item.id) : /*#__PURE__*/_jsx(NavDrawerLinkRow, {
              item: item,
              history: history,
              onNavigate: onClose,
              variant: "top-level"
            }, item.id))
          }), siteMenuData && siteMenuData.items.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(View, {
              paddingBlock: "300",
              children: /*#__PURE__*/_jsx(HR, {
                distance: "flush"
              })
            }), /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              gap: "300",
              paddingBlock: "200",
              paddingInlineStart: "300",
              paddingInlineEnd: "300",
              marginInline: "200",
              "data-test-id": "portals-nav-drawer-quick-links-header",
              children: [/*#__PURE__*/_jsx(LinkIcon, {
                color: "text-core-subtle",
                size: "100"
              }), /*#__PURE__*/_jsx(View, {
                as: "span",
                typography: "body-100",
                color: "text-core-subtle",
                children: unescapedText('app.navDrawer.quickLinks')
              })]
            }), /*#__PURE__*/_jsx(Flex, {
              as: "ul",
              flexDirection: "column",
              gap: "100",
              paddingInline: "200",
              paddingBlockEnd: "400",
              listStyleType: "none",
              children: siteMenuData.items.map((item, index) => /*#__PURE__*/_jsx(NavDrawerSiteMenuItem, {
                item: item,
                level: 0,
                onNavigate: onClose
              }, `sitemenu-${index}`))
            })]
          })]
        })]
      })
    })]
  });
}